import {
  ChatFormData,
  ChatMessage,
  ChatStep,
  CreateTicketPayload,
  SupportTicket,
  SupportTicketListItem,
  TicketCategory
} from 'Interfaces/SupportTickets/supportTicketInterfaces';
import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { supportTicketServices } from 'services/supportTicketServices';
import { handleApiError } from 'utils/handleApiError';
import { v4 as uuidv4 } from 'uuid';

export class SupportTicketStore {
  // Categories
  categories: TicketCategory[] = [];
  isCategoriesLoading: boolean = false;

  // Tickets list
  myTickets: SupportTicketListItem[] = [];
  isTicketsLoading: boolean = false;

  // Selected ticket detail
  selectedTicket: SupportTicket | null = null;
  isTicketDetailLoading: boolean = false;

  // Modals visibility
  isCreateModalVisible: boolean = false;
  isDetailModalVisible: boolean = false;

  // Chat panel
  isChatOpen: boolean = false;
  chatStep: ChatStep = 'category';
  chatMessages: ChatMessage[] = [];

  // Form data
  formData: ChatFormData = {
    category: '',
    title: '',
    description: '',
    attachments: [],
    currentPage: '',
    environment: ''
  };

  // Loading states
  isCreatingTicket: boolean = false;
  isSyncingStatus: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  // ============ Category Actions ============

  fetchCategories = async () => {
    this.isCategoriesLoading = true;
    try {
      const response = await supportTicketServices.getCategories();
      runInAction(() => {
        this.categories = response.categories;
      });
    } catch (error: any) {
      handleApiError(error, 'Failed to fetch ticket categories');
    } finally {
      runInAction(() => {
        this.isCategoriesLoading = false;
      });
    }
  };

  // ============ Ticket Creation Actions ============

  createJSMTicket = async (ticketData: CreateTicketPayload) => {
    this.isCreatingTicket = true;
    try {
      const response = await supportTicketServices.createJSMTicket(ticketData);
      
      // Show success toast with ticket ID
      const ticketId = response.ticket?.jiraTicketId || 'Unknown';
      const message = `Ticket ${ticketId} created successfully!`;
      toast.success(message);
      
      runInAction(() => {
        this.resetChat();
        this.closeChat();
      });
      
      // Refresh tickets list if it was loaded
      if (this.myTickets.length > 0) {
        this.fetchMyTickets();
      }
      
      return response;
    } catch (error: any) {
      // Check if it's a 201 success response
      if (error?.response?.status === 201 && error?.response?.data?.ticket) {
        const ticketId = error.response.data.ticket.jiraTicketId || 'Unknown';
        const message = `Ticket ${ticketId} created successfully!`;
        toast.success(message);
        
        runInAction(() => {
          this.resetChat();
          this.closeChat();
        });
        
        return error.response.data;
      } else {
        handleApiError(error, 'Error creating support ticket');
        throw error;
      }
    } finally {
      runInAction(() => {
        this.isCreatingTicket = false;
      });
    }
  };

  // ============ Tickets List Actions ============

  fetchMyTickets = async (searchQuery?: string) => {
    this.isTicketsLoading = true;
    try {
      const response = await supportTicketServices.getMyTickets(searchQuery);
      runInAction(() => {
        this.myTickets = response.tickets;
      });
    } catch (error: any) {
      handleApiError(error, 'Failed to fetch tickets');
    } finally {
      runInAction(() => {
        this.isTicketsLoading = false;
      });
    }
  };

  // ============ Ticket Detail Actions ============

  fetchTicketDetail = async (ticketId: string) => {
    this.isTicketDetailLoading = true;
    try {
      const response = await supportTicketServices.getTicketDetail(ticketId);
      runInAction(() => {
        this.selectedTicket = response.ticket;
      });
    } catch (error: any) {
      handleApiError(error, 'Failed to fetch ticket details');
    } finally {
      runInAction(() => {
        this.isTicketDetailLoading = false;
      });
    }
  };

  syncTicketStatus = async (ticketId: string) => {
    this.isSyncingStatus = true;
    try {
      const response = await supportTicketServices.syncTicketStatus(ticketId);

      if (response.success) {
        toast.success(response.message || 'Status synced successfully');

        // Refresh ticket detail and list
        await this.fetchTicketDetail(ticketId);
        if (this.myTickets.length > 0) {
          this.fetchMyTickets();
        }
      } else {
        toast.error(response.message || 'Failed to sync status');
      }
    } catch (error: any) {
      handleApiError(error, 'Error syncing ticket status');
    } finally {
      runInAction(() => {
        this.isSyncingStatus = false;
      });
    }
  };

  // ============ Chat Panel Actions ============

  openChat = () => {
    this.isChatOpen = true;

    // Fetch categories if not loaded
    if (this.categories.length === 0) {
      this.fetchCategories();
    }

    // Initialize form with current page
    this.formData.currentPage = window.location.href;
    this.formData.environment = import.meta.env.MODE || 'development';
  };

  closeChat = () => {
    this.isChatOpen = false;
  };

  resetChat = () => {
    this.chatStep = 'category';
    this.chatMessages = [];
    this.formData = {
      category: '',
      title: '',
      description: '',
      attachments: [],
      currentPage: window.location.href,
      environment: import.meta.env.MODE || 'development'
    };
  };

  setChatStep = (step: ChatStep) => {
    this.chatStep = step;
  };

  addChatMessage = (content: string, type: 'user' | 'system' = 'system') => {
    const message: ChatMessage = {
      id: uuidv4(),
      type,
      content,
      timestamp: new Date()
    };
    this.chatMessages.push(message);
  };

  updateFormData = (field: keyof ChatFormData, value: any) => {
    (this.formData as any)[field] = value;
  };

  setFormData = (data: Partial<ChatFormData>) => {
    this.formData = {
      ...this.formData,
      ...data
    };
  };

  // ============ Modal Actions ============

  openDetailModal = async (ticketId: string) => {
    this.isDetailModalVisible = true;
    await this.fetchTicketDetail(ticketId);
  };

  setDetailModalVisible = (visible: boolean) => {
    this.isDetailModalVisible = visible;
    if (!visible) {
      this.selectedTicket = null;
    }
  };

  openCreateModal = () => {
    this.isCreateModalVisible = true;

    // Fetch categories if not loaded
    if (this.categories.length === 0) {
      this.fetchCategories();
    }
  };

  setCreateModalVisible = (visible: boolean) => {
    this.isCreateModalVisible = visible;
  };
}
